import {
  faChevronDown,
  faChevronUp,
  faRoute,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  RoutePlanFeedbackPromptDTO,
  RoutePlanFeedbackRoundEnum,
} from 'api/model';
import { useRoutePlanFeedbackPackage } from 'api/route-plan-feedback/route-plan-feedback';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FEEDBACK_ONLY_NOTE,
  FEEDBACK_QUESTION,
  formatClock,
  formatPlanningDate,
} from './feedback-round-helpers';
import StarRating from '../StarRating';
import PackageView from './PackageView';

const Card = styled.div<{ visible: boolean; wide: boolean }>`
  width: ${({ wide }) =>
    wide ? 'min(980px, calc(100vw - 32px))' : 'min(400px, calc(100vw - 32px))'};
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.foreground.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  padding: 14px;
  display: grid;
  gap: 10px;
  font-size: 0.93rem;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.2s;
`;

const Top = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const Icon = styled.span`
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.background.buttonPale};
  color: ${({ theme }) => theme.colors.foreground.button};
`;

const Body = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.b`
  font-size: 1rem;
`;

const Route = styled.span`
  font-size: 0.85rem;
  background: ${({ theme }) => theme.colors.background.highlight};
  border-radius: 6px;
  padding: 4px 8px;
  overflow-wrap: anywhere;
`;

const Note = styled.span`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const Close = styled.button`
  margin-left: auto;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
  cursor: pointer;
  padding: 0 2px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ primary?: boolean; quiet?: boolean }>`
  border: 1px solid
    ${({ theme, primary, quiet }) =>
      quiet
        ? 'transparent'
        : primary
          ? theme.colors.background.button
          : theme.colors.border.primary};
  background: ${({ theme, primary }) =>
    primary ? theme.colors.background.button : theme.colors.background.primary};
  color: ${({ theme, primary, quiet }) =>
    primary
      ? theme.colors.foreground.button
      : quiet
        ? theme.colors.foreground.tertiary
        : theme.colors.foreground.primary};
  border-radius: 7px;
  padding: 6px 12px;
  font: inherit;
  font-size: 0.88rem;
  font-weight: ${({ quiet }) => (quiet ? 400 : 600)};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

type Phase = 'ask' | 'sending' | 'thanks' | 'error';

const ExpandedArea = styled.div`
  max-height: min(70vh, 760px);
  overflow-y: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  padding-top: 10px;
`;

const Toggle = styled.button`
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.background.button};
  border-radius: 7px;
  padding: 6px 12px;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.background.button};
  cursor: pointer;
`;

const Hint = styled.span`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

interface Props {
  prompt: RoutePlanFeedbackPromptDTO;
  visible: boolean;
  /** Svar direkt i toasten. Löser sig när svaret är sparat. */
  onQuickAnswer: (rating: number) => Promise<void>;
  /** Öppna feedbacksidan på det här paketet. */
  onOpen: () => void;
  /** "Ja, visa fler" efter ett svar: feedbacksidan på nästa paket. */
  onMore: () => void;
  /** "Inte nu" eller krysset före svar. */
  onDismiss: () => void;
  /** Stäng utan att räkna det som "inte nu" (efter ett svar). */
  onClose: () => void;
  /** Utfälld toast stängs aldrig av tidsgränsen — prompten får veta när den fälls ut. */
  onExpandedChange: (expanded: boolean) => void;
}

/**
 * Toasten i kvällens ruttprov. Ställer frågan och visar en kort sammanfattning; "Visa hela
 * förslaget" fäller ut paketet med karta, ben, ut- och hemresa direkt i toasten. Betyget 1–5
 * (samma skala som feedbackknappen i ruttplaneringen) finns först i utfällt läge — ett betyg på en
 * rad text säger inget om förslaget (beslut 2026-10-12: man måste se hela förslaget för att kunna
 * bedöma det). En stjärna väljer betyget och "Skicka" sparar det, så att ett felklick går att
 * rätta innan det räknas.
 */
const RoutePlanFeedbackToast: React.FC<Props> = ({
  prompt,
  visible,
  onQuickAnswer,
  onOpen,
  onMore,
  onDismiss,
  onClose,
  onExpandedChange,
}) => {
  const [phase, setPhase] = useState<Phase>('ask');
  const [expanded, setExpanded] = useState(false);
  const [rating, setRating] = useState<number | undefined>(undefined);
  const pkg = prompt.package;

  const packageQuery = useRoutePlanFeedbackPackage(
    {
      date: prompt.planningDateString,
      packageIndex: pkg.packageIndex,
      // Provet toasten gäller — kommer ett nytt medan den visas ska utfällningen inte byta paket.
      runId: prompt.routePlanRunId,
    },
    { query: { enabled: expanded, staleTime: 5 * 60_000 } }
  );

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    onExpandedChange(next);
  };

  const answer = async () => {
    if (rating === undefined) return;
    setPhase('sending');
    try {
      await onQuickAnswer(rating);
      setPhase('thanks');
    } catch {
      setPhase('error');
    }
  };

  if (phase === 'thanks') {
    return (
      <Card visible={visible} wide={false} role="status">
        <Top>
          <Icon>
            <FontAwesomeIcon icon={faRoute} />
          </Icon>
          <Body>
            <Title>Tack för svaret!</Title>
            <span>Vill du utvärdera fler ruttförslag?</span>
          </Body>
          <Close aria-label="Stäng" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </Close>
        </Top>
        <Actions>
          <ActionButton primary onClick={onMore}>
            Ja, visa fler
          </ActionButton>
          <ActionButton onClick={onOpen}>Lägg till en kommentar</ActionButton>
          <ActionButton quiet onClick={onClose}>
            Nej tack
          </ActionButton>
        </Actions>
      </Card>
    );
  }

  const sending = phase === 'sending';
  const dayLabel =
    prompt.round === RoutePlanFeedbackRoundEnum.Morning
      ? 'i dag'
      : formatPlanningDate(prompt.planningDateString);

  return (
    <Card
      visible={visible}
      wide={expanded}
      role="dialog"
      aria-label="Feedback på ruttförslag"
    >
      <Top>
        <Icon>
          <FontAwesomeIcon icon={faRoute} />
        </Icon>
        <Body>
          <Title>Har du 30 sekunder?</Title>
          <span>{FEEDBACK_QUESTION}</span>
          <Route>
            Förslag för {dayLabel} · #{pkg.packageIndex}
            {pkg.fieldTesterName ? ` · ${pkg.fieldTesterName}` : ''}
            <br />
            {pkg.route || `${pkg.legCount} uppdrag`}
            {pkg.dayStart && pkg.dayEnd
              ? ` · ${formatClock(pkg.dayStart)}–${formatClock(pkg.dayEnd)}`
              : ''}
            {` · ${pkg.legCount} uppdrag`}
          </Route>
          <Note>{FEEDBACK_ONLY_NOTE}</Note>
        </Body>
        <Close aria-label="Inte nu" onClick={onDismiss}>
          <FontAwesomeIcon icon={faXmark} />
        </Close>
      </Top>

      <Toggle type="button" aria-expanded={expanded} onClick={toggle}>
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
        {expanded ? 'Dölj förslaget' : 'Visa hela förslaget'}
      </Toggle>

      {expanded && (
        <ExpandedArea>
          {packageQuery.data ? (
            <PackageView data={packageQuery.data} />
          ) : packageQuery.isError ? (
            <Hint>
              Förslaget kunde inte hämtas. Öppna det på egen sida i stället.
            </Hint>
          ) : (
            <Hint>Hämtar förslaget …</Hint>
          )}
        </ExpandedArea>
      )}

      {phase === 'error' && (
        <Note>
          Svaret kunde inte sparas. Försök igen eller öppna förslaget.
        </Note>
      )}

      {expanded && packageQuery.data ? (
        <>
          <Actions>
            <StarRating
              disabled={sending}
              onChange={setRating}
              size={24}
              value={rating}
            />
          </Actions>
          <Actions>
            <ActionButton
              primary
              disabled={sending || rating === undefined}
              onClick={answer}
            >
              {sending ? 'Skickar …' : 'Skicka'}
            </ActionButton>
            <ActionButton disabled={sending} onClick={onOpen}>
              Öppna på egen sida
            </ActionButton>
            <ActionButton quiet disabled={sending} onClick={onDismiss}>
              Inte nu
            </ActionButton>
          </Actions>
        </>
      ) : (
        <Actions>
          <ActionButton quiet onClick={onDismiss}>
            Inte nu
          </ActionButton>
        </Actions>
      )}
    </Card>
  );
};

export default RoutePlanFeedbackToast;
